// Date display in America/New_York (business timezone, SPEC §0 invariant 8).
// Date-only strings ("2026-08-20") are treated as plain business dates and are
// never timezone-shifted; timestamps are converted to NY time for display.

const NY_TZ = "America/New_York";
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

type DateInput = string | number | Date | null | undefined;

function parse(d: DateInput): { date: Date; dateOnly: boolean } | null {
  if (d == null || d === "") return null;
  if (d instanceof Date) {
    return Number.isNaN(d.getTime()) ? null : { date: d, dateOnly: false };
  }
  if (typeof d === "number") {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? null : { date: dt, dateOnly: false };
  }
  const s = d.trim();
  if (DATE_ONLY_RE.test(s)) {
    const [y, m, day] = s.split("-").map(Number);
    return { date: new Date(Date.UTC(y, m - 1, day)), dateOnly: true };
  }
  const dt = new Date(s);
  return Number.isNaN(dt.getTime()) ? null : { date: dt, dateOnly: false };
}

/** "Aug 20, 2026" in NY time; "—" for null/invalid input. */
export function fmtDate(d: DateInput): string {
  const p = parse(d);
  if (!p) return "—";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: p.dateOnly ? "UTC" : NY_TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(p.date);
}

/** "Aug 20, 2026, 11:05 AM" in NY time; date-only input falls back to fmtDate. */
export function fmtDateTime(d: DateInput): string {
  const p = parse(d);
  if (!p) return "—";
  if (p.dateOnly) return fmtDate(d);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: NY_TZ,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(p.date);
}

/** YYYY-MM-DD of a Date in the given zone (en-CA locale formats exactly that). */
function zonedYmd(date: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

/**
 * Whole weeks elapsed between the given date and today, both taken as NY
 * business dates. Returns null for null/invalid input; negative input dates in
 * the future floor toward -1.
 */
export function weeksAgo(dateStr: string | Date | null | undefined): number | null {
  const p = parse(dateStr);
  if (!p) return null;
  const then = zonedYmd(p.date, p.dateOnly ? "UTC" : NY_TZ);
  const today = zonedYmd(new Date(), NY_TZ);
  const [ty, tm, td] = then.split("-").map(Number);
  const [ny, nm, nd] = today.split("-").map(Number);
  const days = Math.round((Date.UTC(ny, nm - 1, nd) - Date.UTC(ty, tm - 1, td)) / 86_400_000);
  return Math.floor(days / 7);
}
